<template>
  <TopGradientBlockLayout>
    <div class="text-xl font-bold text-text leading-tight mb-7">{{ $t('scheduledOperations') }}</div>
    <div>
      <div v-if="!noTemplatesCreated"
           class="md:-mx-2 lg:-mx-6 schedule-head-grid">
        <div>{{ $t('headLabels.date') }}</div>
        <div>{{ $t('headLabels.account') }}</div>
        <div>{{ $t('headLabels.category') }}</div>
        <div>{{ $t('headLabels.repeat') }}</div>
        <div>{{ $t('headLabels.sum') }}</div>
      </div>
      <div class="space-y-16" v-if="!noTemplatesCreated">
        <div>
          <TransactionsTemplatesList :templates="expenseTemplates.data"
                                     :title="$t('expensesTitle')"
                                     :loading="expenseTemplates.dirty"
                                     @edit="editTemplateStart($event)"
                                     @delete="deleteTemplate(2,$event)"/>
          <div class="flex mt-8">
            <Pagination class="lg:-ml-1"
                        :pagination="expenseTemplates.pagination"
                        @page="changePage(2, $event)"/>
            <PaginationShowMore :pagination="expenseTemplates.pagination"
                                @click.native="changePage(2,expenseTemplates.pagination.current_page+1,true)"/>
          </div>
        </div>
        <div>
          <TransactionsTemplatesList :templates="incomeTemplates.data"
                                     :title="$t('incomesTitle')"
                                     :loading="incomeTemplates.dirty"
                                     @edit="editTemplateStart($event)"
                                     @delete="deleteTemplate(1,$event)"/>
          <div class="flex mt-8">
            <Pagination class="lg:-ml-1"
                        :pagination="incomeTemplates.pagination"
                        @page="changePage(1, $event)"/>
            <PaginationShowMore :pagination="incomeTemplates.pagination"
                                @click.native="changePage(1,incomeTemplates.pagination.current_page+1,true)"/>
          </div>
        </div>
      </div>
    </div>

    <div v-if="noTemplatesCreated"
         class="text-lg font-bold text-text">
      {{ $t('noTemplatesCreated') }}
    </div>
    <PlannedTransactionEditModal position="absolute"
                                 :template="editTemplate"
                                 @close="editTemplate = null"
                                 @success="handleTemplateEditFinished"/>
  </TopGradientBlockLayout>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import TopGradientBlockLayout from "@/components/layouts/cabinet/TopGradientBlockLayout";
import TransactionsTemplatesList from "@/components/cabinet/SpaceSettings/CalendarSettings/TransactionsTemplatesList";
import Pagination from "@/components/UI/Pagination/Pagination";
import PaginationShowMore from "@/components/UI/Pagination/PaginationShowMore";
import PlannedTransactionEditModal
  from "@/components/common/PlannedTransaction/PlannedTransactionEditModal/PlannedTransactionEditModal";

export default {
  name: "CalendarSettings",
  components: {
    PlannedTransactionEditModal,
    PaginationShowMore, Pagination, TransactionsTemplatesList, TopGradientBlockLayout
  },
  data() {
    return {
      expenseTemplates: {
        data: [],
        dirty: false,
        pagination: {
          current_page: 1,
          pages: null
        }
      },
      incomeTemplates: {
        data: [],
        dirty: false,
        pagination: {
          current_page: 1,
          pages: null
        }
      },
      editTemplate: null
    }
  },
  computed: {
    ...mapGetters('space', ['spaceInfo']),
    noTemplatesCreated() {
      return !this.expenseTemplates.data.length
          && !this.incomeTemplates.data.length
          && !this.expenseTemplates.dirty
          && !this.incomeTemplates.dirty;
    }
  },
  methods: {
    ...mapActions('space/plannedTransactions', ['fetchTemplateTransactions', 'archiveTemplateTransaction']),
    async fetchMainData() {
      try {
        this.$store.dispatch('space/accounts/getAccounts');
        this.$store.dispatch('space/accounts/getAccountGroups');
        this.$store.dispatch('space/incomes/getIncomes');
        this.$store.dispatch('space/expenses/getExpenses');
        this.$store.dispatch('space/expenses/getExpenseGroups');
        this.$store.dispatch('space/tags/getTags');
        this.$store.dispatch('space/contractors/getContractors');
      } catch (e) {
        throw e;
      }
    },
    fetchTemplates() {
      this.getTransactionsTemplateByType(1);
      this.getTransactionsTemplateByType(2);
    },
    typeAsObjectName: (type) => type === 1 ? 'incomeTemplates' : 'expenseTemplates',
    async getTransactionsTemplateByType(type, showMore = false) {
      const objName = this.typeAsObjectName(type)
      if (!showMore) this.setTemplatesDirtyByType(type);

      try {
        const query = {
          type,
          space_id: this.spaceInfo.id,
          page: this[objName].pagination.current_page,
          'per-page': 4
        }
        const res = await this.fetchTemplateTransactions(query);
        if (res.body.success) {
          if (showMore) {
            this[objName].data = [...this[objName].data, ...res.body.data.rows]
          } else {
            this[objName].data = res.body.data.rows
          }
          this[objName].pagination.pages = Math.ceil(res.body.data.length / 4);
          this[objName].dirty = false;
        }
      } catch (err) {
        console.error(err);
      }
    },
    setTemplatesDirtyByType(type) {
      const objName = this.typeAsObjectName(type)
      this[objName].dirty = true;
    },
    changePage(type, num, showMore = false) {
      const objName = this.typeAsObjectName(type)
      this[objName].pagination.current_page = num;
      this.getTransactionsTemplateByType(type, showMore)
    },
    async deleteTemplate(type, id) {
      try {
        const res = await this.archiveTemplateTransaction(id);
        if (res.body.success) {
          this.$toasted.success(this.$t('templateRemoved'));
        } else {
          console.error(res)
          this.$toasted.error(this.$t('errors.response'))
        }
      } catch (err) {
        console.error(err)
        this.$toasted.error(this.$t('errors.response'))
      } finally {
        if (type === 1) this.getTransactionsTemplateByType(1);
        if (type === 2) this.getTransactionsTemplateByType(2);
      }
    },
    editTemplateStart(template) {
      this.editTemplate = template;
    },
    async handleTemplateEditFinished() {
      await this.getTransactionsTemplateByType(this.editTemplate.type);
    }
  },
  async mounted() {
    await this.fetchMainData()
    await this.fetchTemplates()
  },
  i18n: {
    messages: {
      en: {
        scheduledOperations: 'Scheduled operations',
        incomesTitle: 'Incomes',
        expensesTitle: 'Expenses',
        templateRemoved: 'Planned operation removed',
        noTemplatesCreated: 'You haven\'t created any scheduled operations',
        headLabels: {
          date: 'Date',
          account: 'Account',
          category: 'Source/Category',
          repeat: 'Repeat',
          sum: 'Sum'
        },
      },
      ru: {
        scheduledOperations: 'Запланированные операции',
        incomesTitle: 'Доходы',
        expensesTitle: 'Расходы',
        templateRemoved: 'Запланированная операция удалена',
        noTemplatesCreated: 'Вы пока не запланировали ни одной операции',
        headLabels: {
          date: 'Дата',
          account: 'Счет',
          category: 'Источник/Категория',
          repeat: 'Повторять',
          sum: 'Сумма'
        },
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule-head-grid {
  @apply grid text-2sm text-text-ternary mb-6 py-3 px-6
  bg-highlight rounded;
  grid-template-columns: 10.8% 23.8% 25.9% 26.8% auto;
}
</style>